import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  FileText,
  FolderOpen,
  BarChart3,
  X,
  Image as ImageIcon,
} from 'lucide-react';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import toast from 'react-hot-toast';
import { ImageUpload } from '@/components/ui/ImageUpload';
import { RichTextEditor } from '@/components/ui/RichTextEditor';
import { LocaleSelector } from '@/components/LocaleSelector';
import { useAdminLocale } from '@/hooks/useAdminLocale';

const supabase = getSupabaseAdminClient();

type StatusFilter = 'all' | 'published' | 'draft';
type TabType = 'posts' | 'categories';

interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  color: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  summary: string | null;
  category: string | null;
  category_id: string | null;
  ssw_week: number | null;
  ssw_min: number | null;
  ssw_max: number | null;
  risk_relevance: 'yes' | 'no' | 'not_relevant';
  child_count_relevance: 'single' | 'multiple' | 'not_relevant';
  image_url: string | null;
  is_published: boolean;
  published_at: string | null;
  view_count: number;
  created_at: string;
  updated_at: string;
  blog_categories?: BlogCategory;
}

type TargetMode = 'pregnancy' | 'baby' | 'all';

interface PostFormData {
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  summary: string;
  category_id: string;
  target_mode: TargetMode;
  ssw_min: number | null;
  ssw_max: number | null;
  risk_relevance: 'yes' | 'no' | 'not_relevant';
  child_count_relevance: 'single' | 'multiple' | 'not_relevant';
  baby_age_min_days: number | null;
  baby_age_max_days: number | null;
  image_url: string;
  is_published: boolean;
}

interface CategoryFormData {
  name: string;
  slug: string;
  color: string;
  description: string;
  sort_order: number;
  is_active: boolean;
}

const initialPostForm: PostFormData = {
  title: '',
  slug: '',
  content: '',
  excerpt: '',
  summary: '',
  category_id: '',
  target_mode: 'pregnancy',
  ssw_min: null,
  ssw_max: null,
  risk_relevance: 'not_relevant',
  child_count_relevance: 'not_relevant',
  baby_age_min_days: null,
  baby_age_max_days: null,
  image_url: '',
  is_published: false,
};

// Helper: convert baby age months to days
const monthsToDays = (months: number): number => Math.round(months * 30.44);
// Helper: convert days to months for display
const daysToMonths = (days: number): number => Math.round(days / 30.44);

const initialCategoryForm: CategoryFormData = {
  name: '',
  slug: '',
  color: '#6366f1',
  description: '',
  sort_order: 0,
  is_active: true,
};

export function BlogPage() {
  const [activeTab, setActiveTab] = useState<TabType>('posts');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showPostModal, setShowPostModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [editingCategory, setEditingCategory] = useState<BlogCategory | null>(null);
  const [postForm, setPostForm] = useState<PostFormData>(initialPostForm);
  const [categoryForm, setCategoryForm] = useState<CategoryFormData>(initialCategoryForm);
  const pageSize = 10;
  const queryClient = useQueryClient();
  const { contentLocale } = useAdminLocale();

  // Fetch blog posts with pagination
  const { data: postsData, isLoading: postsLoading } = useQuery({
    queryKey: ['blog-posts', currentPage, searchQuery, categoryFilter, statusFilter, contentLocale],
    queryFn: async () => {
      let query = supabase
        .from('blog_posts')
        .select('*, blog_categories(*)', { count: 'exact' })
        .eq('locale', contentLocale);

      if (searchQuery) {
        query = query.ilike('title', `%${searchQuery}%`);
      }

      if (categoryFilter !== 'all') {
        query = query.eq('category_id', categoryFilter);
      }

      if (statusFilter === 'published') {
        query = query.eq('is_published', true);
      } else if (statusFilter === 'draft') {
        query = query.eq('is_published', false);
      }

      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      return {
        posts: data as BlogPost[],
        totalCount: count || 0,
      };
    },
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['blog-categories', contentLocale],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_categories')
        .select('*')
        .eq('locale', contentLocale)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as BlogCategory[];
    },
  });

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ['blog-stats'],
    queryFn: async () => {
      const [totalResult, publishedResult, categoriesResult, viewsResult] = await Promise.all([
        supabase.from('blog_posts').select('id', { count: 'exact', head: true }),
        supabase.from('blog_posts').select('id', { count: 'exact', head: true }).eq('is_published', true),
        supabase.from('blog_categories').select('id', { count: 'exact', head: true }).eq('locale', contentLocale),
        supabase.from('blog_posts').select('view_count'),
      ]);

      const totalViews = (viewsResult.data as { view_count: number }[] || [])
        .reduce((sum, post) => sum + (post.view_count || 0), 0);

      return {
        total: totalResult.count || 0,
        published: publishedResult.count || 0,
        categories: categoriesResult.count || 0,
        views: totalViews,
      };
    },
  });

  // Create/Update post mutation
  const savePostMutation = useMutation({
    mutationFn: async (data: PostFormData & { id?: string }) => {
      const { id, ...postData } = data;

      // Generate slug if empty
      if (!postData.slug) {
        postData.slug = postData.title
          .toLowerCase()
          .replace(/[äöüß]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' }[c] || c))
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }

      const payload = {
        ...postData,
        category_id: postData.category_id || null,
        ssw_min: postData.target_mode === 'baby' ? null : (postData.ssw_min || null),
        ssw_max: postData.target_mode === 'baby' ? null : (postData.ssw_max || null),
        risk_relevance: postData.target_mode === 'baby' ? 'not_relevant' : postData.risk_relevance,
        child_count_relevance: postData.target_mode === 'baby' ? 'not_relevant' : postData.child_count_relevance,
        baby_age_min_days: postData.target_mode === 'pregnancy' ? null : (postData.baby_age_min_days || null),
        baby_age_max_days: postData.target_mode === 'pregnancy' ? null : (postData.baby_age_max_days || null),
        image_url: postData.image_url || null,
        published_at: postData.is_published ? new Date().toISOString() : null,
        updated_at: new Date().toISOString(),
        locale: contentLocale,
      };

      if (id) {
        const { error } = await supabase
          .from('blog_posts')
          .update(payload as never)
          .eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert(payload as never);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
      setShowPostModal(false);
      setEditingPost(null);
      setPostForm(initialPostForm);
      toast.success(editingPost ? 'Beitrag aktualisiert' : 'Beitrag erstellt');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Delete post mutation
  const deletePostMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('blog_posts').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
      toast.success('Beitrag gelöscht');
    },
    onError: () => {
      toast.error('Fehler beim Löschen');
    },
  });

  // Toggle publish mutation
  const togglePublishMutation = useMutation({
    mutationFn: async ({ id, isPublished }: { id: string; isPublished: boolean }) => {
      const { error } = await supabase
        .from('blog_posts')
        .update({
          is_published: isPublished,
          published_at: isPublished ? new Date().toISOString() : null,
        } as never)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
      toast.success('Status aktualisiert');
    },
    onError: () => {
      toast.error('Fehler beim Aktualisieren');
    },
  });

  // Create/Update category mutation
  const saveCategoryMutation = useMutation({
    mutationFn: async (data: CategoryFormData & { id?: string }) => {
      const { id, ...categoryData } = data;

      if (!categoryData.slug) {
        categoryData.slug = categoryData.name
          .toLowerCase()
          .replace(/[äöüß]/g, (c) => ({ ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' }[c] || c))
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
      }

      if (id) {
        const { error } = await supabase
          .from('blog_categories')
          .update({ ...categoryData, updated_at: new Date().toISOString() } as never)
          .eq('id', id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('blog_categories')
          .insert({ ...categoryData, locale: contentLocale } as never);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-categories'] });
      queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
      setShowCategoryModal(false);
      setEditingCategory(null);
      setCategoryForm(initialCategoryForm);
      toast.success(editingCategory ? 'Kategorie aktualisiert' : 'Kategorie erstellt');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  // Delete category mutation
  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('blog_categories').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-categories'] });
      queryClient.invalidateQueries({ queryKey: ['blog-stats'] });
      toast.success('Kategorie gelöscht');
    },
    onError: () => {
      toast.error('Fehler beim Löschen (evtl. noch Beiträge zugeordnet)');
    },
  });

  const totalPages = Math.ceil((postsData?.totalCount || 0) / pageSize);

  const openEditPost = (post: BlogPost) => {
    setEditingPost(post);
    setPostForm({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || '',
      summary: post.summary || '',
      category_id: post.category_id || '',
      target_mode: (post as any).target_mode || 'pregnancy',
      ssw_min: post.ssw_min,
      ssw_max: post.ssw_max,
      risk_relevance: post.risk_relevance || 'not_relevant',
      child_count_relevance: post.child_count_relevance || 'not_relevant',
      baby_age_min_days: (post as any).baby_age_min_days ?? null,
      baby_age_max_days: (post as any).baby_age_max_days ?? null,
      image_url: post.image_url || '',
      is_published: post.is_published,
    });
    setShowPostModal(true);
  };

  const openEditCategory = (category: BlogCategory) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      slug: category.slug,
      color: category.color,
      description: category.description || '',
      sort_order: category.sort_order,
      is_active: category.is_active,
    });
    setShowCategoryModal(true);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const getRiskLabel = (risk: string) => {
    switch (risk) {
      case 'yes': return 'Risiko-SS';
      case 'no': return 'Normale SS';
      default: return 'Alle';
    }
  };

  const getChildCountLabel = (count: string) => {
    switch (count) {
      case 'single': return 'Einling';
      case 'multiple': return 'Mehrlinge';
      default: return 'Alle';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Blog-Verwaltung</h1>
          <p className="text-slate-600">Blog-Beiträge und Kategorien verwalten</p>
        </div>
        <LocaleSelector />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.total || 0}</div>
              <div className="text-sm text-slate-600">Beiträge</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.published || 0}</div>
              <div className="text-sm text-slate-600">Veröffentlicht</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.categories || 0}</div>
              <div className="text-sm text-slate-600">Kategorien</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{stats?.views?.toLocaleString('de-DE') || 0}</div>
              <div className="text-sm text-slate-600">Aufrufe</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="border-b border-slate-200">
          <nav className="flex gap-4 px-6">
            <button
              onClick={() => setActiveTab('posts')}
              className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'posts'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Beiträge
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'categories'
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              Kategorien
            </button>
          </nav>
        </div>
      </div>

      {/* Posts Tab */}
      {activeTab === 'posts' && (
        <>
          {/* Filters & Search */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex flex-wrap gap-4 items-center">
              <form onSubmit={handleSearch} className="flex-1 min-w-[300px]">
                <div className="relative">
                  <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Nach Titel suchen..."
                    className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  />
                </div>
              </form>
              <div className="flex gap-2 flex-wrap">
                <select
                  value={categoryFilter}
                  onChange={(e) => {
                    setCategoryFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  <option value="all">Alle Kategorien</option>
                  {categories?.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value as StatusFilter);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  <option value="all">Alle Status</option>
                  <option value="published">Veröffentlicht</option>
                  <option value="draft">Entwurf</option>
                </select>
                <button
                  onClick={() => {
                    setEditingPost(null);
                    setPostForm(initialPostForm);
                    setShowPostModal(true);
                  }}
                  className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Neuer Beitrag
                </button>
              </div>
            </div>
          </div>

          {/* Posts Table */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Titel</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Kategorie</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Modus</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Filter</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Zielgruppe</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Aufrufe</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Aktionen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {postsLoading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                        Lädt...
                      </td>
                    </tr>
                  ) : postsData?.posts?.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                        Keine Beiträge gefunden
                      </td>
                    </tr>
                  ) : (
                    postsData?.posts?.map((post) => (
                      <tr key={post.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {post.image_url ? (
                              <img
                                src={post.image_url}
                                alt=""
                                className="w-10 h-10 rounded object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                                <ImageIcon className="w-5 h-5 text-slate-400" />
                              </div>
                            )}
                            <div>
                              <span className="text-sm font-medium text-slate-900">{post.title}</span>
                              <div className="text-xs text-slate-500">/{post.slug}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {post.blog_categories ? (
                            <span
                              className="px-2 py-1 text-xs rounded-full"
                              style={{
                                backgroundColor: `${post.blog_categories.color}20`,
                                color: post.blog_categories.color,
                              }}
                            >
                              {post.blog_categories.name}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            (post as any).target_mode === 'baby'
                              ? 'bg-pink-100 text-pink-700'
                              : (post as any).target_mode === 'all'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-teal-100 text-teal-700'
                          }`}>
                            {(post as any).target_mode === 'baby' ? 'Baby' : (post as any).target_mode === 'all' ? 'Beide' : 'SS'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600">
                            {(post as any).target_mode === 'baby' || (post as any).target_mode === 'all' ? (
                              <>
                                {(post as any).baby_age_min_days != null || (post as any).baby_age_max_days != null
                                  ? `${(post as any).baby_age_min_days != null ? daysToMonths((post as any).baby_age_min_days) : 0}-${(post as any).baby_age_max_days != null ? daysToMonths((post as any).baby_age_max_days) : '...'} Mon.`
                                  : 'Alle Alter'}
                              </>
                            ) : (
                              <>
                                {post.ssw_min && post.ssw_max
                                  ? `SSW ${post.ssw_min}-${post.ssw_max}`
                                  : post.ssw_min
                                  ? `ab SSW ${post.ssw_min}`
                                  : post.ssw_max
                                  ? `bis SSW ${post.ssw_max}`
                                  : 'Alle SSW'}
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1">
                            <span className="text-xs text-slate-500">
                              {getRiskLabel(post.risk_relevance)}
                            </span>
                            <span className="text-xs text-slate-500">
                              {getChildCountLabel(post.child_count_relevance)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => togglePublishMutation.mutate({ id: post.id, isPublished: !post.is_published })}
                            className="focus:outline-none"
                          >
                            {post.is_published ? (
                              <span className="flex items-center gap-1 text-emerald-600 text-sm">
                                <CheckCircle className="w-4 h-4" />
                                Live
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-slate-400 text-sm">
                                <XCircle className="w-4 h-4" />
                                Entwurf
                              </span>
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-600">{post.view_count?.toLocaleString('de-DE') || 0}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button className="p-1 hover:bg-slate-100 rounded" title="Vorschau">
                              <Eye className="w-4 h-4 text-slate-500" />
                            </button>
                            <button
                              onClick={() => openEditPost(post)}
                              className="p-1 hover:bg-slate-100 rounded"
                              title="Bearbeiten"
                            >
                              <Edit className="w-4 h-4 text-slate-500" />
                            </button>
                            <button
                              onClick={() => {
                                if (confirm('Beitrag wirklich löschen?')) {
                                  deletePostMutation.mutate(post.id);
                                }
                              }}
                              className="p-1 hover:bg-slate-100 rounded"
                              title="Löschen"
                            >
                              <Trash2 className="w-4 h-4 text-slate-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
              <div className="text-sm text-slate-600">
                Zeige {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, postsData?.totalCount || 0)} von {postsData?.totalCount?.toLocaleString('de-DE') || 0}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 border border-slate-300 rounded ${
                      currentPage === page
                        ? 'bg-teal-500 text-white border-teal-500'
                        : 'hover:bg-slate-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages || 1, p + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-3 py-1 border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-200 flex justify-between items-center">
            <h3 className="font-semibold text-slate-900">Kategorien</h3>
            <button
              onClick={() => {
                setEditingCategory(null);
                setCategoryForm(initialCategoryForm);
                setShowCategoryModal(true);
              }}
              className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neue Kategorie
            </button>
          </div>
          <div className="divide-y divide-slate-200">
            {categories?.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Keine Kategorien vorhanden
              </div>
            ) : (
              categories?.map((category) => (
                <div key={category.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                  <div className="flex items-center gap-4">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    <div>
                      <div className="font-medium text-slate-900">{category.name}</div>
                      <div className="text-sm text-slate-500">/{category.slug}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`text-sm ${category.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {category.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditCategory(category)}
                        className="p-1 hover:bg-slate-100 rounded"
                        title="Bearbeiten"
                      >
                        <Edit className="w-4 h-4 text-slate-500" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Kategorie wirklich löschen?')) {
                            deleteCategoryMutation.mutate(category.id);
                          }
                        }}
                        className="p-1 hover:bg-slate-100 rounded"
                        title="Löschen"
                      >
                        <Trash2 className="w-4 h-4 text-slate-500" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Post Modal */}
      {showPostModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-slate-900">
                {editingPost ? 'Beitrag bearbeiten' : 'Neuer Beitrag'}
              </h2>
              <button
                onClick={() => {
                  setShowPostModal(false);
                  setEditingPost(null);
                  setPostForm(initialPostForm);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                savePostMutation.mutate({ ...postForm, id: editingPost?.id });
              }}
              className="p-6 space-y-6"
            >
              {/* Title & Slug */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Titel *
                  </label>
                  <input
                    type="text"
                    value={postForm.title}
                    onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Slug
                  </label>
                  <input
                    type="text"
                    value={postForm.slug}
                    onChange={(e) => setPostForm({ ...postForm, slug: e.target.value })}
                    placeholder="wird-automatisch-generiert"
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Kategorie
                </label>
                <select
                  value={postForm.category_id}
                  onChange={(e) => setPostForm({ ...postForm, category_id: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                >
                  <option value="">Keine Kategorie</option>
                  {categories?.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Image Upload */}
              <ImageUpload
                value={postForm.image_url}
                onChange={(url) => setPostForm({ ...postForm, image_url: url })}
                bucket="BlogTitleImages"
                label="Titelbild"
                placeholder="https://example.com/bild.jpg"
              />

              {/* Target Mode Toggle */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ziel-Modus
                </label>
                <div className="flex rounded-lg border border-slate-300 overflow-hidden">
                  {([
                    { value: 'pregnancy', label: 'Schwangerschaft', color: 'teal' },
                    { value: 'baby', label: 'Baby', color: 'pink' },
                    { value: 'all', label: 'Beide', color: 'purple' },
                  ] as const).map((mode) => (
                    <button
                      key={mode.value}
                      type="button"
                      onClick={() => setPostForm({ ...postForm, target_mode: mode.value })}
                      className={`flex-1 py-2 text-sm font-medium transition-colors ${
                        postForm.target_mode === mode.value
                          ? mode.color === 'teal'
                            ? 'bg-teal-500 text-white'
                            : mode.color === 'pink'
                            ? 'bg-pink-500 text-white'
                            : 'bg-purple-500 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Schwangerschaft-Filter (shown for pregnancy and all) */}
              {(postForm.target_mode === 'pregnancy' || postForm.target_mode === 'all') && (
                <>
                  {/* SSW Range */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        SSW von
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="42"
                        value={postForm.ssw_min ?? ''}
                        onChange={(e) => setPostForm({ ...postForm, ssw_min: e.target.value ? parseInt(e.target.value) : null })}
                        placeholder="1"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        SSW bis
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="42"
                        value={postForm.ssw_max ?? ''}
                        onChange={(e) => setPostForm({ ...postForm, ssw_max: e.target.value ? parseInt(e.target.value) : null })}
                        placeholder="42"
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                      />
                    </div>
                  </div>

                  {/* Risk & Child Count */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Risiko-Schwangerschaft
                      </label>
                      <select
                        value={postForm.risk_relevance}
                        onChange={(e) => setPostForm({ ...postForm, risk_relevance: e.target.value as 'yes' | 'no' | 'not_relevant' })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                      >
                        <option value="not_relevant">Nicht relevant (alle)</option>
                        <option value="yes">Nur Risiko-SS</option>
                        <option value="no">Nur normale SS</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Kinderzahl
                      </label>
                      <select
                        value={postForm.child_count_relevance}
                        onChange={(e) => setPostForm({ ...postForm, child_count_relevance: e.target.value as 'single' | 'multiple' | 'not_relevant' })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
                      >
                        <option value="not_relevant">Nicht relevant (alle)</option>
                        <option value="single">Nur Einling</option>
                        <option value="multiple">Nur Mehrlinge</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Baby-Filter (shown for baby and all) */}
              {(postForm.target_mode === 'baby' || postForm.target_mode === 'all') && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Baby-Alter ab (Monate)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="36"
                      value={postForm.baby_age_min_days != null ? daysToMonths(postForm.baby_age_min_days) : ''}
                      onChange={(e) => setPostForm({ ...postForm, baby_age_min_days: e.target.value ? monthsToDays(parseInt(e.target.value)) : null })}
                      placeholder="0"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Baby-Alter bis (Monate)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="36"
                      value={postForm.baby_age_max_days != null ? daysToMonths(postForm.baby_age_max_days) : ''}
                      onChange={(e) => setPostForm({ ...postForm, baby_age_max_days: e.target.value ? monthsToDays(parseInt(e.target.value)) : null })}
                      placeholder="36"
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                    />
                  </div>
                </div>
              )}

              {/* Excerpt - Rich Text Editor */}
              <RichTextEditor
                value={postForm.excerpt}
                onChange={(html) => setPostForm({ ...postForm, excerpt: html })}
                label="Kurzfassung (Excerpt)"
                placeholder="Kurze Vorschau für Listenansichten..."
                minHeight="100px"
              />

              {/* Summary - Rich Text Editor */}
              <RichTextEditor
                value={postForm.summary}
                onChange={(html) => setPostForm({ ...postForm, summary: html })}
                label="Zusammenfassung"
                placeholder="Ausführlichere Zusammenfassung..."
                minHeight="150px"
              />

              {/* Content - Rich Text Editor */}
              <RichTextEditor
                value={postForm.content}
                onChange={(html) => setPostForm({ ...postForm, content: html })}
                label="Inhalt *"
                placeholder="Schreibe hier den Beitrag..."
                minHeight="300px"
              />

              {/* Publish Toggle */}
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={postForm.is_published}
                    onChange={(e) => setPostForm({ ...postForm, is_published: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500" />
                </label>
                <span className="text-sm font-medium text-slate-700">
                  {postForm.is_published ? 'Veröffentlichen' : 'Als Entwurf speichern'}
                </span>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowPostModal(false);
                    setEditingPost(null);
                    setPostForm(initialPostForm);
                  }}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={savePostMutation.isPending}
                  className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {savePostMutation.isPending ? 'Speichert...' : (editingPost ? 'Aktualisieren' : 'Erstellen')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-slate-900">
                {editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
              </h2>
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setEditingCategory(null);
                  setCategoryForm(initialCategoryForm);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                saveCategoryMutation.mutate({ ...categoryForm, id: editingCategory?.id });
              }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Slug
                </label>
                <input
                  type="text"
                  value={categoryForm.slug}
                  onChange={(e) => setCategoryForm({ ...categoryForm, slug: e.target.value })}
                  placeholder="wird-automatisch-generiert"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Farbe
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                    className="w-12 h-10 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={categoryForm.color}
                    onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Beschreibung
                </label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Reihenfolge
                </label>
                <input
                  type="number"
                  value={categoryForm.sort_order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                />
              </div>

              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={categoryForm.is_active}
                    onChange={(e) => setCategoryForm({ ...categoryForm, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500" />
                </label>
                <span className="text-sm font-medium text-slate-700">Aktiv</span>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategory(null);
                    setCategoryForm(initialCategoryForm);
                  }}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saveCategoryMutation.isPending}
                  className="px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium disabled:opacity-50"
                >
                  {saveCategoryMutation.isPending ? 'Speichert...' : (editingCategory ? 'Aktualisieren' : 'Erstellen')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
